import { STORE_MUTATION } from "../../enums";
import { store } from "../../store/store";
import { sanitizeHtml } from "../sanitize";

export class DynamicAccessibilityHandler {
  
  private static sanitizeHtmlForSpeech(html: string): string {
    if (!html) return "";

    // 1. Initial Sanitization
    // We must include 'aria-label' in the ALLOWED_ATTR list now
    let text = sanitizeHtml(html, { 
        FORBID_TAGS: ['style', 'script', 'svg', 'iframe', 'object'],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'aria-label'] 
    });

    // 2. Handle Block Elements (Pauses)
    text = text.replace(/<\/(div|p|section|article|h[1-6])>/gi, '. '); 
    text = text.replace(/<br\s*\/?>/gi, '. ');

    // 3. Handle Lists with aria-label support
    // Logic: If <ul aria-label="Choices"> exists, announce "Choices List starts:"
    text = text.replace(/<ul\b[^>]*aria-label=(["'])(.*?(?<!\\))\1[^>]*>/gi, ' $2 List starts: ');
    text = text.replace(/<ul\b[^>]*>/gi, ' List starts: ');
    text = text.replace(/<\/ul>/gi, ' List ends. ');

    text = text.replace(/<ol\b[^>]*aria-label=(["'])(.*?(?<!\\))\1[^>]*>/gi, ' $2 Numbered list starts: ');
    text = text.replace(/<ol\b[^>]*>/gi, ' Numbered list starts: ');
    text = text.replace(/<\/ol>/gi, ' List ends. ');

    // 4. Handle List Items with aria-label support
    text = text.replace(/<li\b[^>]*aria-label=(["'])(.*?(?<!\\))\1[^>]*>/gi, ' Bullet point: $2. ');
    text = text.replace(/<li\b[^>]*>/gi, ' Bullet point: ');
    text = text.replace(/<\/li>/gi, '. ');

    // 5. Handle Buttons with aria-label support
    text = text.replace(/<button\b[^>]*aria-label=(["'])(.*?(?<!\\))\1[^>]*>(.*?)<\/button>/gi, ' Button: $2. ');
    text = text.replace(/<button\b[^>]*>(.*?)<\/button>/gi, ' Button: $1. ');

    // 6. Handle Headings
    text = text.replace(/<h([1-6])\b[^>]*>/gi, ' Heading level $1: ');

    // 7. Handle Images
    text = text.replace(/<img\b[^>]*(?:aria-label|alt)=(["'])(.*?(?<!\\))\1[^>]*>/gi, (match, quote, content) => {
        return ` Image: ${content}. `;
    });

    // 8. Handle Links
    text = text.replace(/<a\b[^>]*aria-label=(["'])(.*?(?<!\\))\1[^>]*>(.*?)<\/a>/gi, ' Link: $2. ');
    text = text.replace(/<a\b[^>]*>(.*?)<\/a>/gi, ' Link: $1. ');

    // 9. Handle Generic Elements fallback
    text = text.replace(/<[^>]*aria-label=(["'])(.*?(?<!\\))\1[^>]*>/gi, ' $2. ');

    // 10. Strip ALL remaining tags
    text = text.replace(/<[^>]+>/g, ' ');
    
    // 11. Clean up backslashes
    text = text.replace(/\\(['"])/g, '$1');

    // 12. Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    text = textarea.value;

    // 13. Final Cleanup
    text = text.replace(/\s+/g, ' ').trim();
    text = text.replace(/\.+/g, '.');
    text = text.replace(/\.\s+\./g, '. ');

    return text;
  }
  
  
  private static extractText (el: HTMLElement) {
    const clone = el.cloneNode(true) as HTMLElement;       

    // 1. Define the "silent" selectors (Attributes + Classes)
    const silentSelectors = [
        '[aria-hidden="true"]',
        '[style*="display: none"]',
        '[style*="display:none"]',
        '[style*="visibility: hidden"]',
        '[style*="visibility:hidden"]',
        // '.satisfi_visually-hidden' /* In-case of any class element to be ignored */
    ].join(', ');

    // 2. Remove them from the clone
    const silentElements = clone.querySelectorAll(silentSelectors);
    silentElements.forEach(el => el.remove());

    // 3. Process the remaining "clean" HTML
    const cleanHtml = clone.outerHTML;

    if (cleanHtml) {
        /* Sanitized texts to read by screen readers */
        return this.sanitizeHtmlForSpeech(cleanHtml);
    }
  }

  /**
   * JIRA SD-10865: Generic announcer that can take any HTML element 
   * and convert its content into a screen-reader friendly announcement.
   * @param element - The HTMLElement to extract content from.
   */
  static announceElementContent(element: HTMLElement | (HTMLElement | string)[] | null): void {
      if (!element) return;

      try {
        
        
        let spokenText = "";
        
        if (element instanceof HTMLElement) {
            spokenText = this.extractText(element);
        } else {
            element.forEach(el => spokenText += typeof el === "string" ? el : this.extractText(el));
        }
        
        if (spokenText) {
            this.announcementContent(spokenText);
        }
      } catch (ex) {
          console.error("Error in generic announcer:", ex);
      }
  }
  
  
  static announcementContent(value?: string): void {
      store.commit(STORE_MUTATION.SetScreenReaderAnnouncement, value);
  }
}